import { Suspense, lazy } from "react";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/common/ThemeProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { SuperAdminRoute } from "@/components/auth/SuperAdminRoute";
import { RoleRoute } from "@/components/auth/RoleRoute";
import { FeatureGate } from "@/components/auth/FeatureGate";
import { AppShell } from "@/components/layout/AppShell";
import { AdminShell } from "@/components/admin/AdminShell";
import { FieldShell } from "@/components/layout/FieldShell";
import { PageLoader } from "@/components/common/PageLoader";

// Landing page v2 — lazy (Sprint LP-Rebuild Abril 2026)
const SoluxLandingPageV2 = lazy(() => import("./pages/public/SoluxLandingPageV2"));

// All other pages — lazy loaded to reduce initial bundle
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Register = lazy(() => import("./pages/Register"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SalesPage = lazy(() => import("./pages/public/SalesPage"));
const ProposalAcceptPage = lazy(() => import("./pages/public/ProposalAcceptPage"));
const QuotationPublicPage = lazy(() => import("./pages/public/QuotationPublicPage"));
const ContractSignPage = lazy(() => import("./pages/public/ContractSignPage"));
const AccessBlocked = lazy(() => import("./pages/app/AccessBlocked"));
const ClientPortalPage = lazy(() => import("./pages/public/ClientPortalPage"));
const EngineeringPortal = lazy(() => import("./pages/public/EngineeringPortal"));
const InstallationPortal = lazy(() => import("./pages/public/InstallationPortal"));
const TermosDeUsoPage = lazy(() => import("./pages/public/TermosDeUsoPage"));
const PrivacidadePage = lazy(() => import("./pages/public/PrivacidadePage"));
const DpaPage = lazy(() => import("./pages/public/DpaPage"));
const Onboarding = lazy(() => import("./pages/app/Onboarding"));

// Lazy-loaded protected pages
const Dashboard = lazy(() => import("./pages/app/Dashboard"));
const Configuracoes = lazy(() => import("./pages/app/Configuracoes"));
const Documentos = lazy(() => import("./pages/app/Documentos"));
const IntegracoesPage = lazy(() => import("./pages/app/IntegracoesPage"));
const CRMLayout = lazy(() => import("./pages/app/crm/CRMLayout"));
const LeadsPage = lazy(() => import("./pages/app/crm/LeadsPage"));
const DealsPage = lazy(() => import("./pages/app/crm/DealsPage"));
const CRMRelatoriosPage = lazy(() => import("./pages/app/crm/CRMRelatoriosPage"));
const OpportunityDetailPage = lazy(() => import("./pages/app/crm/OpportunityDetailPage"));
const LeadDetailPage = lazy(() => import("./pages/app/crm/LeadDetailPage"));
const TasksPage = lazy(() => import("./pages/app/TasksPage"));
const AtividadesPage = lazy(() => import("./pages/app/AtividadesPage"));
const InstaladorPage = lazy(() => import("./pages/app/InstaladorPage"));
const LogisticaPage = lazy(() => import("./pages/app/LogisticaPage"));
const InstalacoesNewPage = lazy(() => import("./pages/app/InstalacoesNewPage"));
const CampoLogisticaPage = lazy(() => import("./pages/app/campo/CampoLogisticaPage"));
const CampoInstaladorPage = lazy(() => import("./pages/app/campo/CampoInstaladorPage"));
const Financeiro = lazy(() => import("./pages/app/Financeiro"));
const ProdutosServicosPage = lazy(() => import("./pages/app/ProdutosServicosPage"));
const FornecedoresPage = lazy(() => import("./pages/app/cadastros/FornecedoresPage"));
const ParceirosPage = lazy(() => import("./pages/app/cadastros/ParceirosPage"));
const ContatosPage = lazy(() => import("./pages/app/cadastros/ContatosPage"));

const ProjectsPage = lazy(() => import("./pages/app/ProjectsPage"));
const SalesPageEditor = lazy(() => import("./pages/app/SalesPageEditor"));

// Admin pages (lazy-loaded)
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminTenants = lazy(() => import("./pages/admin/AdminTenants"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminPlans = lazy(() => import("./pages/admin/AdminPlans"));
const AdminIntegracoes = lazy(() => import("./pages/admin/AdminIntegracoes"));
const AdminSeguranca = lazy(() => import("./pages/admin/AdminSeguranca"));
const AdminTenantDetailPage = lazy(() => import("./pages/admin/AdminTenantDetailPage"));
const AdminComunicacao = lazy(() => import("./pages/admin/AdminComunicacao"));
const AdminAuditLog = lazy(() => import("./pages/admin/AdminAuditLog"));
const AdminLoginBanners = lazy(() => import("./pages/admin/AdminLoginBanners"));
const AdminLandingPage = lazy(() => import("./pages/admin/AdminLandingPage"));
const AdminTemplates = lazy(() => import("./pages/admin/AdminTemplates"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminImpact = lazy(() => import("./pages/admin/AdminImpact"));
const AdminLegalAcceptances = lazy(() => import("./pages/admin/AdminLegalAcceptances"));
const AdminWhatsAppHub = lazy(() => import("./pages/admin/AdminWhatsAppHub"));
const AdminProjectsPage = lazy(() => import("./pages/admin/AdminProjectsPage"));
const AdminProjectDetailPage = lazy(() => import("./pages/admin/AdminProjectDetailPage"));
const AdminProspectingPage = lazy(() => import("./pages/admin/AdminProspectingPage"));
const AdminArchitectureDecisions = lazy(() => import("./pages/admin/AdminArchitectureDecisions"));
const AdminHelpEditor = lazy(() => import("./pages/admin/AdminHelpEditor"));
const AdminUiTranslations = lazy(() => import("./pages/admin/AdminUiTranslations"));
const AdminChangelog = lazy(() => import("./pages/admin/AdminChangelog"));
const ChangelogPage = lazy(() => import("./pages/app/ChangelogPage"));
const ProspectingPage = lazy(() => import("./pages/app/ProspectingPage"));
const MinhaContaPage = lazy(() => import("./pages/app/MinhaContaPage"));
const HelpPage = lazy(() => import("./pages/app/HelpPage"));
const WhatsAppInboxPage = lazy(() => import("./pages/app/WhatsAppInboxPage"));
const WhatsAppCampaignsPage = lazy(() => import("./pages/app/WhatsAppCampaignsPage"));
const CampaignDetailPage = lazy(() => import("./components/whatsapp-campaigns/CampaignDetailPage"));
const SegmentsPage = lazy(() => import("./pages/app/SegmentsPage"));
const SegmentBuilderPage = lazy(() => import("./pages/app/SegmentBuilderPage"));
const ContactListDetailPage = lazy(() => import("./pages/app/ContactListDetailPage"));
const WhatsAppAutomationsPage = lazy(() => import("./pages/app/WhatsAppAutomationsPage"));
const WhatsAppFlowBuilderPage = lazy(() => import("./pages/app/WhatsAppFlowBuilderPage"));

// Mockup (temporário — Sprint 8B preview)
const DashboardMockup = lazy(() => import("./pages/_mockups/DashboardMockup"));
const CrmPipelineMockup = lazy(() => import("./pages/_mockups/CrmPipelineMockup"));
const CrmDetailMockup = lazy(() => import("./pages/_mockups/CrmDetailMockup"));
const CrmContatosMockup = lazy(() => import("./pages/_mockups/CrmContatosMockup"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: 1,
      retryDelay: 1000,
    },
    mutations: {
      retry: 0,
    },
  },
});

const PageFallback = () => <PageLoader />;

const ErrorFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background p-4">
    <div className="text-center space-y-4 max-w-sm">
      <h2 className="text-lg font-semibold text-foreground">Algo deu errado</h2>
      <p className="text-sm text-muted-foreground">Um erro inesperado ocorreu. Tente recarregar a página.</p>
      <button
        onClick={() => window.location.reload()}
        className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        Recarregar página
      </button>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
        <BrowserRouter>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
          <Routes>
            {/* Landing page v2 */}
            <Route path="/" element={<Suspense fallback={<PageFallback />}><SoluxLandingPageV2 /></Suspense>} />
            
            {/* Public routes */}
            <Route path="/login" element={<Suspense fallback={<PageFallback />}><Login /></Suspense>} />
            <Route path="/forgot-password" element={<Suspense fallback={<PageFallback />}><ForgotPassword /></Suspense>} />
            <Route path="/reset-password" element={<Suspense fallback={<PageFallback />}><ResetPassword /></Suspense>} />
            <Route path="/register" element={<Suspense fallback={<PageFallback />}><Register /></Suspense>} />
            <Route path="/s/:slug" element={<Suspense fallback={<PageFallback />}><SalesPage /></Suspense>} />
            <Route path="/p/proposta/:slug" element={<Suspense fallback={<PageFallback />}><ProposalAcceptPage /></Suspense>} />
            <Route path="/p/cotacao/:slug" element={<Suspense fallback={<PageFallback />}><QuotationPublicPage /></Suspense>} />
            <Route path="/p/contrato/:slug" element={<Suspense fallback={<PageFallback />}><ContractSignPage /></Suspense>} />
            <Route path="/portal/:slug" element={<Suspense fallback={<PageFallback />}><ClientPortalPage /></Suspense>} />
            <Route path="/ext/engenharia/:token" element={<Suspense fallback={<PageFallback />}><EngineeringPortal /></Suspense>} />
            <Route path="/ext/instalacao/:token" element={<Suspense fallback={<PageFallback />}><InstallationPortal /></Suspense>} />
            <Route path="/acesso-bloqueado" element={<Suspense fallback={<PageFallback />}><AccessBlocked /></Suspense>} />
            <Route path="/termos-de-uso" element={<Suspense fallback={<PageFallback />}><TermosDeUsoPage /></Suspense>} />
            <Route path="/privacidade" element={<Suspense fallback={<PageFallback />}><PrivacidadePage /></Suspense>} />
            <Route path="/dpa" element={<Suspense fallback={<PageFallback />}><DpaPage /></Suspense>} />

            {/* Mockup público temporário — Sprint 8B preview */}
            <Route path="/mockup/dashboard" element={<Suspense fallback={<PageFallback />}><DashboardMockup /></Suspense>} />
            <Route path="/mockup/crm-pipeline" element={<Suspense fallback={<PageFallback />}><CrmPipelineMockup /></Suspense>} />
            <Route path="/mockup/crm-detail" element={<Suspense fallback={<PageFallback />}><CrmDetailMockup /></Suspense>} />
            <Route path="/mockup/crm-contatos" element={<Suspense fallback={<PageFallback />}><CrmContatosMockup /></Suspense>} />

            {/* Onboarding route - for users without tenant */}
            <Route
              path="/app/onboarding"
              element={
                <ProtectedRoute requireTenant={false}>
                  <Suspense fallback={<PageFallback />}><Onboarding /></Suspense>
                </ProtectedRoute>
              }
            />

            {/* ══ Field App routes (clean shell, no sidebar) ══ */}
            <Route
              path="/app/campo"
              element={
                <ProtectedRoute requireTenant={true}>
                  <FieldShell />
                </ProtectedRoute>
              }
            >
              <Route path="logistica" element={
                <Suspense fallback={<PageFallback />}>
                  <RoleRoute permission="canViewLogistica">
                    <FeatureGate featureKey="instalacoes">
                      <CampoLogisticaPage />
                    </FeatureGate>
                  </RoleRoute>
                </Suspense>
              } />
              <Route path="instalador" element={
                <Suspense fallback={<PageFallback />}>
                  <RoleRoute permission="canViewInstalacoes">
                    <FeatureGate featureKey="instalacoes">
                      <CampoInstaladorPage />
                    </FeatureGate>
                  </RoleRoute>
                </Suspense>
              } />
            </Route>

            {/* Protected routes - require tenant */}
            <Route
              path="/app"
              element={
                <ProtectedRoute requireTenant={true}>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<Suspense fallback={<PageFallback />}><Dashboard /></Suspense>} />
              
              {/* CRM Module — ALL CRM subroutes inside the gate */}
              <Route path="crm" element={
                <FeatureGate featureKey="crm">
                  <Suspense fallback={<PageFallback />}><CRMLayout /></Suspense>
                </FeatureGate>
              }>
                <Route index element={<Navigate to="deals" replace />} />
                <Route path="deals" element={<Suspense fallback={<PageFallback />}><DealsPage /></Suspense>} />
                <Route path="leads" element={<Suspense fallback={<PageFallback />}><LeadsPage /></Suspense>} />
                <Route path="relatorios" element={
                  <Suspense fallback={<PageFallback />}>
                    <RoleRoute permission="canViewConfiguracoes">
                      <CRMRelatoriosPage />
                    </RoleRoute>
                  </Suspense>
                } />
              </Route>

              {/* CRM internal routes — also gated by CRM feature */}
              <Route path="crm/oportunidade/:id" element={
                <FeatureGate featureKey="crm">
                  <Suspense fallback={<PageFallback />}><OpportunityDetailPage /></Suspense>
                </FeatureGate>
              } />
              <Route path="crm/lead/:id" element={
                <FeatureGate featureKey="crm">
                  <Suspense fallback={<PageFallback />}><LeadDetailPage /></Suspense>
                </FeatureGate>
              } />

              {/* Atividades — gated */}
              <Route path="atividades" element={
                <FeatureGate featureKey="atividades">
                  <Suspense fallback={<PageFallback />}><AtividadesPage /></Suspense>
                </FeatureGate>
              } />
              {/* Tasks → redirect to atividades */}
              <Route path="tasks" element={<Navigate to="/app/atividades" replace />} />

              {/* Mensagens WhatsApp — gated */}
              <Route path="mensagens" element={
                <FeatureGate featureKey="whatsapp_hub">
                  <Suspense fallback={<PageFallback />}><WhatsAppInboxPage /></Suspense>
                </FeatureGate>
              } />

              {/* Campanhas WhatsApp — gated */}
              <Route path="campanhas-whatsapp" element={
                <FeatureGate featureKey="whatsapp_hub">
                  <Suspense fallback={<PageFallback />}><WhatsAppCampaignsPage /></Suspense>
                </FeatureGate>
              } />
              <Route path="campanhas-whatsapp/segmentos" element={
                <FeatureGate featureKey="whatsapp_hub">
                  <RoleRoute permission="canManageCampaigns">
                    <Suspense fallback={<PageFallback />}><SegmentsPage /></Suspense>
                  </RoleRoute>
                </FeatureGate>
              } />
              <Route path="campanhas-whatsapp/segmentos/novo" element={
                <FeatureGate featureKey="whatsapp_hub">
                  <RoleRoute permission="canManageCampaigns">
                    <Suspense fallback={<PageFallback />}><SegmentBuilderPage /></Suspense>
                  </RoleRoute>
                </FeatureGate>
              } />
              <Route path="campanhas-whatsapp/segmentos/:id/editar" element={
                <FeatureGate featureKey="whatsapp_hub">
                  <RoleRoute permission="canManageCampaigns">
                    <Suspense fallback={<PageFallback />}><SegmentBuilderPage /></Suspense>
                  </RoleRoute>
                </FeatureGate>
              } />
              <Route path="campanhas-whatsapp/listas/:id" element={
                <FeatureGate featureKey="whatsapp_hub">
                  <RoleRoute permission="canManageCampaigns">
                    <Suspense fallback={<PageFallback />}><ContactListDetailPage /></Suspense>
                  </RoleRoute>
                </FeatureGate>
              } />
              <Route path="campanhas-whatsapp/:id" element={
                <FeatureGate featureKey="whatsapp_hub">
                  <Suspense fallback={<PageFallback />}><CampaignDetailPage /></Suspense>
                </FeatureGate>
              } />

              {/* Automações WhatsApp — gated */}
              <Route path="automacoes-whatsapp" element={
                <FeatureGate featureKey="whatsapp_hub">
                  <Suspense fallback={<PageFallback />}><WhatsAppAutomationsPage /></Suspense>
                </FeatureGate>
              } />
              <Route path="automacoes-whatsapp/fluxo/:id" element={
                <FeatureGate featureKey="whatsapp_hub">
                  <Suspense fallback={<PageFallback />}><WhatsAppFlowBuilderPage /></Suspense>
                </FeatureGate>
              } />

              {/* Logística & Instalações — protected by FeatureGate + RoleRoute */}
              <Route path="logistica" element={
                <Suspense fallback={<PageFallback />}>
                  <RoleRoute permission="canViewLogistica">
                    <FeatureGate featureKey="logistica">
                      <LogisticaPage />
                    </FeatureGate>
                  </RoleRoute>
                </Suspense>
              } />
              <Route path="instalacoes" element={
                <Suspense fallback={<PageFallback />}>
                  <RoleRoute permission="canViewInstalacoes">
                    <FeatureGate featureKey="instalacoes">
                      <InstalacoesNewPage />
                    </FeatureGate>
                  </RoleRoute>
                </Suspense>
              } />
              <Route path="instalador" element={
                <Suspense fallback={<PageFallback />}>
                  <RoleRoute permission="canViewInstalacoes">
                    <FeatureGate featureKey="instalacoes">
                      <InstaladorPage />
                    </FeatureGate>
                  </RoleRoute>
                </Suspense>
              } />

            {/* Clientes — redirect to unified CRM Contatos */}
              <Route path="clientes" element={<Navigate to="/app/crm/leads" replace />} />

              {/* Financeiro Module - protected by role + feature */}
              <Route path="financeiro" element={
                <FeatureGate featureKey="financeiro">
                  <Suspense fallback={<PageFallback />}>
                    <RoleRoute permission="canViewFinanceiro"><Financeiro /></RoleRoute>
                  </Suspense>
                </FeatureGate>
              } />
              <Route path="financeiro/recorrencias" element={
                <FeatureGate featureKey="financeiro">
                  <Suspense fallback={<PageFallback />}>
                    <RoleRoute permission="canViewFinanceiro">{(() => { const RecorrenciasPage = lazy(() => import("./pages/app/financeiro/RecorrenciasPage")); return <RecorrenciasPage />; })()}</RoleRoute>
                  </Suspense>
                </FeatureGate>
              } />

              {/* Produtos & Serviços — protected */}
              <Route path="produtos-servicos" element={
                <Suspense fallback={<PageFallback />}>
                  <RoleRoute permission="canViewCRM"><ProdutosServicosPage /></RoleRoute>
                </Suspense>
              } />

              {/* Cadastros — protected */}
              <Route path="cadastros/contatos" element={
                <Suspense fallback={<PageFallback />}>
                  <RoleRoute permission="canViewCRM"><ContatosPage /></RoleRoute>
                </Suspense>
              } />
              <Route path="cadastros/fornecedores" element={
                <Suspense fallback={<PageFallback />}>
                  <RoleRoute permission="canViewCRM"><FornecedoresPage /></RoleRoute>
                </Suspense>
              } />
              <Route path="cadastros/parceiros" element={
                <Suspense fallback={<PageFallback />}>
                  <RoleRoute permission="canViewCRM"><ParceirosPage /></RoleRoute>
                </Suspense>
              } />

              <Route path="documentos" element={
                <Suspense fallback={<PageFallback />}><Documentos /></Suspense>
              } />
              <Route path="configuracoes" element={
                <Suspense fallback={<PageFallback />}><Configuracoes /></Suspense>
              } />
              <Route path="minha-conta" element={
                <Suspense fallback={<PageFallback />}><MinhaContaPage /></Suspense>
              } />
              <Route path="integracoes" element={
                <Suspense fallback={<PageFallback />}>
                  <RoleRoute permission="canViewIntegracoes"><IntegracoesPage /></RoleRoute>
                </Suspense>
              } />
              {/* Sales Page Editor — gated by sales_page feature */}
              <Route path="pagina-vendas" element={
                <FeatureGate featureKey="sales_page">
                  <Suspense fallback={<PageFallback />}>
                    <RoleRoute permission="canManageTenant"><SalesPageEditor /></RoleRoute>
                  </Suspense>
                </FeatureGate>
              } />
              {/* Projetos — admin only */}
              <Route path="projetos" element={
                <RoleRoute permission="canManageTenant">
                  <Suspense fallback={<PageFallback />}><ProjectsPage /></Suspense>
                </RoleRoute>
              } />
              {/* Help page */}
              <Route path="ajuda" element={<Suspense fallback={<PageFallback />}><HelpPage /></Suspense>} />
              {/* Redirect old status route */}
              <Route path="status" element={<Navigate to="/app/integracoes" replace />} />
              {/* Prospecção — gated */}
              <Route path="prospeccao" element={
                <FeatureGate featureKey="prospeccao_bdr">
                  <Suspense fallback={<PageFallback />}><ProspectingPage /></Suspense>
                </FeatureGate>
              } />
              {/* Novidades — público autenticado */}
              <Route path="novidades" element={<Suspense fallback={<PageFallback />}><ChangelogPage /></Suspense>} />
            </Route>

            {/* Super Admin routes */}
            <Route
              path="/admin"
              element={
                <SuperAdminRoute>
                  <AdminShell />
                </SuperAdminRoute>
              }
            >
              <Route index element={<Suspense fallback={<PageFallback />}><AdminDashboard /></Suspense>} />
              <Route path="tenants" element={<Suspense fallback={<PageFallback />}><AdminTenants /></Suspense>} />
              <Route path="tenants/:id" element={<Suspense fallback={<PageFallback />}><AdminTenantDetailPage /></Suspense>} />
              <Route path="reports" element={<Suspense fallback={<PageFallback />}><AdminReports /></Suspense>} />
              <Route path="impact" element={<Suspense fallback={<PageFallback />}><AdminImpact /></Suspense>} />
              <Route path="users" element={<Suspense fallback={<PageFallback />}><AdminUsers /></Suspense>} />
              <Route path="plans" element={<Suspense fallback={<PageFallback />}><AdminPlans /></Suspense>} />
              <Route path="templates" element={<Suspense fallback={<PageFallback />}><AdminTemplates /></Suspense>} />
              <Route path="integracoes" element={<Suspense fallback={<PageFallback />}><AdminIntegracoes /></Suspense>} />
              <Route path="comunicacao" element={<Suspense fallback={<PageFallback />}><AdminComunicacao /></Suspense>} />
              <Route path="login-banners" element={<Suspense fallback={<PageFallback />}><AdminLoginBanners /></Suspense>} />
              <Route path="landing-page" element={<Suspense fallback={<PageFallback />}><AdminLandingPage /></Suspense>} />
              <Route path="auditoria" element={<Suspense fallback={<PageFallback />}><AdminAuditLog /></Suspense>} />
              <Route path="seguranca" element={<Suspense fallback={<PageFallback />}><AdminSeguranca /></Suspense>} />
              <Route path="legal" element={<Suspense fallback={<PageFallback />}><AdminLegalAcceptances /></Suspense>} />
              <Route path="whatsapp" element={<Suspense fallback={<PageFallback />}><AdminWhatsAppHub /></Suspense>} />
              <Route path="projetos" element={<Suspense fallback={<PageFallback />}><AdminProjectsPage /></Suspense>} />
              <Route path="projetos/:tenantId" element={<Suspense fallback={<PageFallback />}><AdminProjectDetailPage /></Suspense>} />
              {/* Prospecção BDR — Super Admin */}
              <Route path="prospeccao" element={<Suspense fallback={<PageFallback />}><AdminProspectingPage /></Suspense>} />
              {/* Decisões Arquiteturais (ADRs) — Super Admin */}
              <Route path="adrs" element={<Suspense fallback={<PageFallback />}><AdminArchitectureDecisions /></Suspense>} />
              {/* Editor de Ajuda — Super Admin */}
              <Route path="ajuda" element={<Suspense fallback={<PageFallback />}><AdminHelpEditor /></Suspense>} />
              {/* Traduções de UI — Super Admin */}
              <Route path="traducoes" element={<Suspense fallback={<PageFallback />}><AdminUiTranslations /></Suspense>} />
              {/* Editor de Changelog — Super Admin */}
              <Route path="changelog" element={<Suspense fallback={<PageFallback />}><AdminChangelog /></Suspense>} />
            </Route>

            {/* Fallback for expired/invalid external links */}
            <Route path="/ext/*" element={
              <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
                <div className="text-center space-y-3 max-w-sm">
                  <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                    <svg className="w-6 h-6 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86l-8.58 14.14A2 2 0 003.44 21h17.12a2 2 0 001.73-3l-8.58-14.14a2 2 0 00-3.46 0z" /></svg>
                  </div>
                  <h2 className="text-lg font-semibold text-foreground">Link não encontrado ou expirado</h2>
                  <p className="text-sm text-muted-foreground">Solicite um novo link ao responsável.</p>
                </div>
              </div>
            } />

            {/* 404 catch-all */}
            <Route path="*" element={<Suspense fallback={<PageFallback />}><NotFound /></Suspense>} />
          </Routes>
          </Sentry.ErrorBoundary>
        </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
